import * as React from 'react'
import { Badge, Container, Row, Col, Collapse } from 'react-bootstrap'
import { PositionRewardStatus } from 'api/withdrawal'

interface RewardStatusBadgesProps {
  positions: PositionRewardStatus[]
}

type PaymentCycle = 'daily' | 'monthly'

/**
 * 支払サイクル表示コンポーネント
 */
const RewardStatusBadges: React.FC<RewardStatusBadgesProps> = ({ positions }) => {
  const [expandedBadge, setExpandedBadge] = React.useState<PaymentCycle | null>(null)

  // Count positions by type
  const dailyPositions = positions.filter((p) => p.isDailyRewardUser)
  const monthlyPositions = positions.filter((p) => !p.isDailyRewardUser)
  const dailyCount = dailyPositions.length
  const monthlyCount = monthlyPositions.length

  const toggleDetail = (type: PaymentCycle) => {
    setExpandedBadge(expandedBadge === type ? null : type)
  }

  const getBadgeStyle = (type: PaymentCycle, count: number, isExpanded: boolean): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 16px',
      borderRadius: '16px',
      fontSize: '13px',
      cursor: count > 0 ? 'pointer' : 'not-allowed',
      transition: 'all 0.3s ease',
      userSelect: 'none',
      position: 'relative',
      border: 'none',
    }

    if (count === 0) {
      return {
        ...baseStyle,
        opacity: 0.5,
        background: '#e0e0e0',
        color: '#999',
        fontWeight: 'normal',
      }
    }

    const typeStyles =
      type === 'daily'
        ? {
            background: 'linear-gradient(135deg, #00695c 0%, #00897b 100%)',
            color: 'white',
            fontWeight: '600',
            boxShadow: isExpanded ? '0 4px 15px rgba(0, 105, 92, 0.4)' : '0 3px 12px rgba(0, 105, 92, 0.3)',
          }
        : {
            background: 'linear-gradient(135deg, #757575 0%, #9e9e9e 100%)',
            color: 'white',
            fontWeight: '600',
            boxShadow: isExpanded ? '0 4px 15px rgba(117, 117, 117, 0.4)' : '0 3px 12px rgba(117, 117, 117, 0.3)',
          }

    return {
      ...baseStyle,
      ...typeStyles,
      transform: isExpanded ? 'scale(1.05)' : 'scale(1)',
    }
  }

  const positionCountStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.25)',
    padding: '2px 8px',
    borderRadius: '10px',
    fontWeight: 'bold',
    marginLeft: '4px',
  }

  const expandArrowStyle = (isExpanded: boolean): React.CSSProperties => ({
    marginLeft: '6px',
    transition: 'transform 0.3s ease',
    fontSize: '11px',
    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    display: 'inline-block',
  })

  const detailContainerStyle: React.CSSProperties = {
    background: '#f8f9fa',
    borderRadius: '8px',
    padding: '15px',
    marginTop: '10px',
    marginBottom: '10px',
    fontSize: '13px',
  }

  const detailLabelStyle = (type: PaymentCycle): React.CSSProperties => ({
    fontWeight: '600',
    minWidth: '80px',
    color: type === 'daily' ? '#00695c' : '#757575',
    fontSize: '13px',
  })

  return (
    <>
      <Container fluid>
        <Row className="justify-content-center mb-3">
          <Col xs="auto" className="d-flex gap-2 flex-wrap justify-content-center">
            {/* Daily Badge */}
            <Badge
              bg="none"
              style={getBadgeStyle('daily', dailyCount, expandedBadge === 'daily')}
              aria-disabled={dailyCount === 0}
              onClick={() => dailyCount > 0 && toggleDetail('daily')}
              className="d-flex align-items-center"
            >
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
              <span>日次報酬確定者</span>
              <span style={positionCountStyle}>×{dailyCount}</span>
              <span style={expandArrowStyle(expandedBadge === 'daily')}>▼</span>
            </Badge>

            {/* Monthly Badge */}
            <Badge
              bg="none"
              style={getBadgeStyle('monthly', monthlyCount, expandedBadge === 'monthly')}
              aria-disabled={monthlyCount === 0}
              onClick={() => monthlyCount > 0 && toggleDetail('monthly')}
              className="d-flex align-items-center"
            >
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
              <span>月次報酬確定者</span>
              <span style={positionCountStyle}>×{monthlyCount}</span>
              <span style={expandArrowStyle(expandedBadge === 'monthly')}>▼</span>
            </Badge>
          </Col>
        </Row>

        {/* Daily Detail Container */}
        {dailyCount > 0 && (
          <Row className="justify-content-center">
            <Col className="g-0">
              <Collapse in={expandedBadge === 'daily'}>
                <div style={detailContainerStyle}>
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <div style={detailLabelStyle('daily')}>日次確定：</div>
                    <div className="text-start" style={{ color: '#555', fontSize: '13px', flex: 1 }}>
                      {dailyPositions.map((p) => p.position_label).join(', ')}
                    </div>
                  </div>
                </div>
              </Collapse>
            </Col>
          </Row>
        )}

        {/* Monthly Detail Container */}
        {monthlyCount > 0 && (
          <Row className="justify-content-center">
            <Col className="g-0">
              <Collapse in={expandedBadge === 'monthly'}>
                <div style={detailContainerStyle}>
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <div style={detailLabelStyle('monthly')}>月次確定：</div>
                    <div className="text-start" style={{ color: '#555', fontSize: '13px', flex: 1 }}>
                      {monthlyPositions.map((p) => p.position_label).join(', ')}
                    </div>
                  </div>
                </div>
              </Collapse>
            </Col>
          </Row>
        )}
      </Container>
    </>
  )
}

export default RewardStatusBadges
